import React, {
  cloneElement,
  Dispatch,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import omit from 'lodash.omit';
import {
  AddPicturesIcon,
  Button,
  DisplayMode,
  Heading,
  LoadingOverlay,
  MatchesMediaQuery,
} from '@taopix/taopix-design-system';
import {useAssetServices} from '../../Context/AssetServices/AssetServicesContext';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {CalendarSelector, LayoutSelector} from '../../../Selectors';
import {useDispatch, useSelector} from 'react-redux';
import {ActionType} from '@taopix/taopix-core/dist/types/types';
import {AssetStatus, AssistantType, CollectionType, CoreActions, Lang} from '@taopix/taopix-core';
import MediaGallery from '../../MediaGallery/MediaGallery';
import {AssistantHeader} from '../AssistantHeader';
import {AssistantFooter} from '../AssistantFooter';
import {AssistantContent} from '../AssistantContent';
import {AssistantSubHeader} from '../AssistantSubHeader';
import {AssistantNav} from '../AssistantNav';
import {useAssetHelperService} from '../../../Hooks/Assets/useAssetHelperService';
import {useTaopix} from '../../Context/TaopixContext';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import useStatusChecker from '../../../Hooks/Assets/useStatusChecker';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import {DropZoneProvider} from '../../Context/Upload/UploadContext';
import {AssistantAssetsProps, AssistantCommonProps, AssistantNavigationProps} from '../Assistant';
import {useUserFeedback} from '../../Context/UserFeedback/UserFeedbackContext';
import {UserFeedbackActions} from '../../Context/UserFeedback/UserFeedbackActionTypes';
import {AssetServicesActions} from '../../Context/AssetServices/AssetServicesActions';
import {useAssetStats} from '../../Context/Assets/AssetStatsContext';
import {useExperience} from '../../Experience/ExperienceProvider';
import useSettings from '../../../Hooks/useSettings';
import AddPicturesButton from '../../AssetServices/AddPicturesButton';

type ManageAssetsStepProps = {} & AssistantCommonProps &
  AssistantAssetsProps &
  AssistantNavigationProps;

export const ManageAssetsStep = ({
  assets,
  onClose,
  backButton,
  mediaGalleryProps,
  ...props
}: ManageAssetsStepProps) => {
  const dispatch = useDispatch<Dispatch<ActionType<typeof CoreActions>>>();
  const app = useTaopix();
  const ui = useUiState();
  const assetServiceContext = useAssetServices();
  const assetHelper = useAssetHelperService(app.core.get('server'));
  const monitor = useStatusChecker();
  const settings = useSettings();
  const userFeedback = useUserFeedback();
  const {t} = useTranslation(['*', 'uploadexternalassets']);
  // const verySmallScreen = MatchesMediaQuery('(max-height:400px)');
  const {getNotReadyAssets} = useAssetStats();
  const productLayout = useSelector(LayoutSelector);
  const assetUploading = useRef(false);
  const [assetUploadInProgress, setAssetUploadInProgress] = React.useState(false);
  const {experience} = useExperience();
  const switchInProgress = useRef(false);
  const {
    state: {totalPercentage},
  } = useAssetStats();

  // Check if there are assets on mount, this means that the user has gone back
  // If there are no assets then it's a brand new project.
  const hasAssetOnMount = useRef(assets.length > 0);

  const [notReadyAssetsReal] = getNotReadyAssets();
  const [notReadyAssets] = useMemo(() => {
    // Only return the real value if an upload is not in progress.
    // Handles the situation where you add multiple batches of images.
    if (assetUploading.current) return [-1, []];
    return [notReadyAssetsReal, []];
  }, [assetUploading.current, notReadyAssetsReal]);

  // get calendar settings from store
  const calendarStoreSettings = useSelector(CalendarSelector)?.settings;

  const [minPictures, maxPictures] = useMemo(() => {
    const {addPicturesStep} = settings.state.project;

    if (
      CollectionType.Calendar === productLayout.type &&
      'undefined' !== typeof calendarStoreSettings.numberOfMonths
    ) {
      // Standard assistant is a fixed calendar.
      // Advanced is a replace layout calendar.
      if (AssistantType.Standard === experience.WIZARD.designAssistantType.designAssistantType) {
        // It's a fixed calendar.
        // Fixed calendars include the cover in the min and max.
        return [addPicturesStep.min, addPicturesStep.max];
      }

      return [
        calendarStoreSettings.numberOfMonths * addPicturesStep.min +
          (addPicturesStep.coverDetails?.min ?? 1),
        calendarStoreSettings.numberOfMonths * addPicturesStep.max +
          (addPicturesStep.coverDetails?.max ?? 1),
      ];
    }

    return [addPicturesStep.min, addPicturesStep.max];
  }, [
    productLayout.type,
    productLayout.code,
    calendarStoreSettings?.numberOfMonths,
    calendarStoreSettings?.startMonth,
    calendarStoreSettings?.startYear,
  ]);

  useEffect(() => {
    if (assetUploading.current && getNotReadyAssets()[0] === 0) {
      setImagesAdded(false);
    }
  }, [assets.length, getNotReadyAssets()[0]]);

  const setImagesAdded = (added: boolean) => {
    assetUploading.current = added;
    setAssetUploadInProgress(added);
  };

  const onToggleServiceList = (anchor: string | undefined, useParentWidth: boolean) => {
    const state = {...assetServiceContext.state};
    if (anchor) {
      state.anchor = anchor;
    }
    state.open = !state.open;
    state.useParentWidth = useParentWidth;

    assetServiceContext.dispatch(AssetServicesActions.setOpenState(state));
  };

  /**
   * Remove an asset from a project.
   *
   * @param id ID of the asset to delete.
   */
  const onDeleteAsset = React.useCallback(
    (id: number) => {
      const asset = assets.find(asset => asset.id === id);
      if (!asset) {
        return;
      }

      ui.dispatch(UserInterfaceActions.setIsBusy(true));

      assetHelper
        .onDeleteAssets([asset])
        .then(response => {
          if (monitor && response.success.includes(id)) {
            dispatch(CoreActions.deleteAsset(id));

            if (monitor.includes(id)) {
              monitor.remove(id);
            }
          } else {
            // TODO: feedback
          }
        })
        .catch(error => {
          userFeedback.dispatch(
            UserFeedbackActions.setErrorUserFeedback({
              title: '',
              message: error.message,
              showHeader: true,
            })
          );
        })
        .finally(() => {
          ui.dispatch(UserInterfaceActions.setIsBusy(false));
        });
    },
    [assets.length]
  );

  const onRotateAsset = (id: number) => {
    const asset = assets.find(asset => asset.id === id);
    if (!asset) {
      return;
    }

    const current = asset.rotation;
    let rotation = current - 90;

    if (rotation < 0) {
      rotation += 360;
    }

    // dispatch the event straight away to prevent a lag between the design system and the UI
    dispatch(CoreActions.updateAsset({...asset, rotation}));

    assetHelper.onRotateAsset(asset).catch(error => {
      // on error, return the rotation back to its original state
      dispatch(CoreActions.updateAsset({...asset, rotation: current}));
    });
  };

  // Add pictures button.
  // const pictureButtonId = 'assistant-add-pictures';
  // const addPicturesButton = (
  //   <Button
  //     buttonStyle="primary"
  //     label={assets.length > 0 ? t('str_ButtonAddMorePictures') : t('str_ButtonAddPictures')}
  //     onClick={() =>
  //       onToggleServiceList(pictureButtonId, assets.length === 0 && !ui.state.isSmallScreen)
  //     }
  //     aria-pressed={
  //       assetServiceContext.state.open && assetServiceContext.state.anchor === pictureButtonId
  //     }
  //     id={pictureButtonId}
  //     startIcon={assets.length === 0 ? <AddPicturesIcon /> : undefined}
  //     hideLabel={verySmallScreen}
  //   />
  // );

  const getNextButton = useCallback(() => {
    const disabled =
      assets.length === 0 ||
      assetUploadInProgress ||
      notReadyAssets > 0 ||
      (minPictures > 0 && assets.length < minPictures);

    return cloneElement(props.nextButton, {
      ...props.nextButton.props,
      disabled: disabled,
      onClick: () => {
        if (disabled) return false;

        // Second check here if images have been added,
        // this is because the button may not get re-rendered in time and will still be enabled
        // Use the ref value as it's updated quicker.
        if (assetUploading.current) return false;

        props.nextButton.props.onClick();
      },
    });
  }, [
    assets.length,
    notReadyAssets,
    assetUploadInProgress,
    Lang.getCode(),
    ui.state.isSmallScreen,
    assetUploading.current,
  ]);

  const getHeaderLabel = (): ReactElement => {
    const dragLabel = 'str_MessageDropPicturesHere';
    const tapLabel = 'str_LabelPleaseAddSomePictures';
    let headerLabel = <></>;

    if (minPictures > 0) {
      let label = '';
      if (minPictures === maxPictures) {
        if (minPictures === 1) {
          label = 'str_LabelRequiresExactlyPicture';
        } else {
          label = 'str_LabelRequiresExactlyPictures';
        }
      } else {
        label = 'str_LabelPleaseAddAtLeastPictures';
      }

      headerLabel = (
        <Heading className={'text-center'}>{t(label).replace('^0', String(minPictures))}</Heading>
      );
    } else {
      headerLabel = (
        // Show an appropriate message based on the device's input capabilities. Devices with touch AND mouse will see the drag message.
        <>
          <Heading className={'touch:hidden mouse:!inline text-center'}>{t(dragLabel)}</Heading>
          <Heading className={'mouse:hidden text-center'}>{t(tapLabel)}</Heading>
        </>
      );
    }

    return headerLabel;
  };

  const nextButton = getNextButton();

  const dragMessageClasses = classNames(
    'touch:hidden mouse:!inline content-center items-center text-center mt-sm'
  );

  const tapToAddMessageClasses = classNames(
    'touch:!inline mouse:hidden content-center items-center text-center mt-sm'
  );

  // Check if any dialogs are open.
  const dialogsReady = !userFeedback.state.open && !assetServiceContext.state.serviceDialogOpen;

  const showReviewStep = (() => {
    if (AssistantType.SmartDesign !== experience.WIZARD.designAssistantType.designAssistantType) {
      if (hasAssetOnMount.current) {
        return true;
      } else if (assets.length > 0 && minPictures > 0 && assets.length < minPictures) {
        // Minimum asset requirement not met so show the review step.
        return true;
      }
      return experience.WIZARD.addPicturesStep.showReviewStep;
    }
    return true;
  })();

  useEffect(() => {
    if (AssistantType.SmartDesign === experience.WIZARD.designAssistantType.designAssistantType)
      return;
    if (showReviewStep) return;
    if (switchInProgress.current) return;
    if (assetUploading.current) return;
    const assetsReady = !assetUploading.current && assets.length > 0 && 0 === notReadyAssets;

    switch (experience.WIZARD.designAssistantType.designAssistantType) {
      case AssistantType.PictureSelectionOnly: {
        if (
          !experience.WIZARD.pictureSelectionOnlyOptions.autoFlowSelectedPicturesAtProjectStart &&
          assets.length > 0 &&
          !switchInProgress.current
        ) {
          // Go straight to the Editor if automatic autoflow is not configured, no need to wait for assets to finish processing.
          switchInProgress.current = true;
          props.nextButton.props.onClick();
          return;
        } else if (
          assetsReady &&
          experience.WIZARD.pictureSelectionOnlyOptions.autoFlowSelectedPicturesAtProjectStart &&
          dialogsReady &&
          !switchInProgress.current
        ) {
          // To run autoflow, we must wait for images to finish uploading.
          switchInProgress.current = true;
          props.nextButton.props.onClick();
          return;
        }
        break;
      }
      case AssistantType.Standard:
      case AssistantType.Advanced: {
        if (
          assetsReady &&
          dialogsReady &&
          assets.length >= minPictures &&
          !switchInProgress.current
        ) {
          switchInProgress.current = true;
          props.nextButton.props.onClick();
          return;
        }
        break;
      }
    }
  }, [
    showReviewStep,
    assetUploading.current,
    assets.length,
    totalPercentage,
    notReadyAssets,
    userFeedback.state.open,
    assetServiceContext.state.serviceDialogOpen,
    switchInProgress.current,
    dialogsReady,
  ]);

  if (
    AssistantType.PictureSelectionOnly ===
      experience.WIZARD.designAssistantType.designAssistantType &&
    !showReviewStep &&
    !assetServiceContext.state.serviceDialogOpen &&
    assets.length > 0
  ) {
    if (userFeedback.state.open) return <></>;

    const label = (() => {
      if (assets.length > 0 && totalPercentage < 100) {
        return t('*:str_TitlePreparingPictures');
      }

      return t('*:str_TitlePleaseWait');
    })();
    return <LoadingOverlay open={true} label={label} delay={0} />;
  }

  return (
    <>
      <DropZoneProvider
        container={'body'}
        catchZone={{
          zIndex: 2050,
          content: <p>{t('str_MessageDropPicturesNow')}</p>,
          classList: 'bg-[rgb(121,179,94)]/70',
        }}
      >
        <AssistantHeader displayMode={props.displayMode}></AssistantHeader>
        <AssistantNav
          onClose={onClose}
          backButton={backButton}
          nextButton={nextButton}
          onNext={nextButton.props.onClick}
        />
        {assets.length > 0 && (
          <AssistantSubHeader
            assetCount={assets.length}
            minSelection={minPictures}
            // addPicturesButton={addPicturesButton}
            assetsWithError={assets.filter(asset => AssetStatus.Error === asset.status)}
          />
        )}
        <AssistantContent className={'lg:p-lg sm:p-sm'}>
          {assets.length > 0 ? (
            <MediaGallery
              {...mediaGalleryProps}
              selectableImages={false}
              showPreviewButton={true}
              onDeleteAsset={onDeleteAsset}
              onRotateAsset={onRotateAsset}
              overlayDisplayMode={'static'}
              showDeleteButton={true}
              showLabel={false}
            />
          ) : (
            <>
              <div
                id={'dropzone'}
                className={
                  'flex-1 m-[5vh] pt-[50rem] lg:border-dashed lg:border-2 lg:border-themeTextColour30 lg:rounded-themeCornerSize items-center flex flex-col'
                }
              >
                {getHeaderLabel()}
                <p className={dragMessageClasses}>{t('str_MessageDragPictures') as string}</p>
                <p className={tapToAddMessageClasses}>{t('str_MessageTapAddPictures') as string}</p>

                {/* {React.cloneElement(addPicturesButton, {
                  className: classNames('min-w-[200rem]', 'mt-lg'),
                })} */}

                <AddPicturesButton
                  buttonProps={{className: classNames('min-w-[200rem]', 'mt-lg')}}
                  onFilesAdded={() => {
                    setImagesAdded(true);
                  }}
                />
              </div>
            </>
          )}
        </AssistantContent>
        <AssistantFooter />
      </DropZoneProvider>
      <LoadingOverlay
        open={notReadyAssets > 0 || (assets.length > 0 && !showReviewStep && dialogsReady)}
        label={t('*:str_TitlePreparingPictures')}
        delay={0}
      />
    </>
  );
};
